import { useContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import "./ProAnalyticsSection.css";

/* ── Load Chart.js once via script tag (CDN-safe) ─────────── */
let chartJsLoaded = false;
let chartJsPromise = null;

function loadChartJs() {
  if (chartJsLoaded) return Promise.resolve();
  if (chartJsPromise) return chartJsPromise;
  chartJsPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js";
    script.onload = () => { chartJsLoaded = true; resolve(); };
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return chartJsPromise;
}

/* ── Insight derivation (unchanged) ──────────────────────── */
function deriveInsights(metrics, rawData) {
  const insights = [];
  const velocity = metrics.weeklyVelocity || [0, 0, 0, 0];
  const thisWeek = velocity[0];
  const lastWeek = velocity[1];
  const GOAL_HRS = 5;

  if (thisWeek < lastWeek * 0.85 && lastWeek > 0) {
    const gap = (lastWeek - thisWeek).toFixed(1);
    const extraWeeks = Math.ceil((GOAL_HRS - thisWeek) / Math.max(0.5, thisWeek)) - 1;
    insights.push({
      type: "warn",
      text: `You're ${gap}h behind last week's pace. At this rate your track takes ${extraWeeks > 0 ? extraWeeks + " extra week" + (extraWeeks > 1 ? "s" : "") : "longer than planned"}.`,
      action: "Build a catch-up plan",
      prompt: "Give me a focused study plan to recover my weekly learning pace based on my current track phase",
    });
  } else if (thisWeek >= GOAL_HRS) {
    insights.push({
      type: "ok",
      text: `You've already hit ${thisWeek}h this week — above your ${GOAL_HRS}h goal. Maintain this pace and you'll finish the track ahead of schedule.`,
      action: null,
    });
  }

  if (metrics.avgQuizScore > 0 && metrics.avgQuizScore < 70) {
    insights.push({
      type: "warn",
      text: `Your quiz average is ${metrics.avgQuizScore}% — below the 70% threshold. Focused review before moving phases will compound.`,
      action: "What to review",
      prompt: "What concepts should I focus on to improve my quiz scores in my current track?",
    });
  }

  const daysToInterview = typeof metrics.estimatedWeeks === "number"
    ? metrics.estimatedWeeks * 7 : null;

  if (daysToInterview && daysToInterview > 90) {
    const fasterDays = Math.round(daysToInterview * 0.6);
    insights.push({
      type: "info",
      text: `At current pace you'll be ready in ~${metrics.estimatedWeeks} weeks. Increasing to ${GOAL_HRS}h/week trims that to ~${Math.round(fasterDays / 7)} weeks.`,
      action: "How to speed up",
      prompt: "How can I increase my learning velocity to finish my current track faster without burning out?",
    });
  }

  if (
    rawData?.streak?.currentStreak < 3 &&
    rawData?.streak?.longestStreak > 7
  ) {
    insights.push({
      type: "warn",
      text: `Your streak is ${rawData.streak.currentStreak} days — but you've hit ${rawData.streak.longestStreak} days before. You know you can maintain it.`,
      action: null,
    });
  }

  return insights;
}

function findWeakDay(heatmapData) {
  if (!heatmapData || heatmapData.length < 14) return null;
  const byDow = Array(7).fill(0).map(() => ({ total: 0, count: 0 }));
  heatmapData.forEach((d) => {
    const dow = new Date(d.date).getDay();
    byDow[dow].total += d.minutes;
    byDow[dow].count += 1;
  });
  const avgs = byDow.map((d, i) => ({
    dow: i,
    avg: d.count > 0 ? d.total / d.count : 0,
  }));
  const sorted = [...avgs].sort((a, b) => a.avg - b.avg);
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return dayNames[sorted[0].dow];
}

/* ── Check whether all velocity values are zero ─────────── */
function hasRealData(velocity = []) {
  return velocity.some((v) => v > 0);
}

/* ── Velocity Chart sub-component ────────────────────────── */
function VelocityChart({ velocity = [0, 0, 0, 0] }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [ready, setReady] = useState(chartJsLoaded);

  useEffect(() => {
    let cancelled = false;
    loadChartJs().then(() => {
      if (!cancelled) setReady(true);
    }).catch((err) => {
      console.error("Chart.js failed to load:", err);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!ready || !canvasRef.current) return;
    const Chart = window.Chart;
    if (!Chart) return;

    // Destroy any previous instance cleanly
    if (chartRef.current) {
      try { chartRef.current.destroy(); } catch (_) { }
      chartRef.current = null;
    }

    const data = [...velocity].reverse(); // oldest → newest
    const labels = ["3 wks ago", "2 wks ago", "Last week", "This week"];
    const maxY = Math.max(6, ...data.map((v) => v || 0)) + 0.5;

    // Detect CSS color vars for dark mode compatibility
    const style = getComputedStyle(document.documentElement);
    const textMuted = style.getPropertyValue("--text-muted").trim() || "#64748b";
    const borderColor = style.getPropertyValue("--border").trim() || "rgba(0,0,0,0.07)";

    chartRef.current = new Chart(canvasRef.current, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Hours studied",
            data,
            borderColor: "#378ADD",
            backgroundColor: "rgba(55,138,221,0.07)",
            borderWidth: 2,
            pointBackgroundColor: "#378ADD",
            pointBorderColor: "#fff",
            pointBorderWidth: 1.5,
            pointRadius: 4,
            pointHoverRadius: 6,
            fill: true,
            tension: 0.38,
          },
          {
            label: "Weekly goal",
            data: [5, 5, 5, 5],
            borderColor: textMuted,
            borderDash: [5, 4],
            borderWidth: 1.2,
            pointRadius: 0,
            fill: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) =>
                ctx.dataset.label === "Weekly goal"
                  ? " Goal: 5h"
                  : ` ${(ctx.parsed.y || 0).toFixed(1)}h`,
            },
          },
        },
        scales: {
          x: {
            grid: { color: borderColor },
            ticks: { font: { size: 11 }, color: textMuted },
          },
          y: {
            min: 0,
            max: maxY,
            grid: { color: borderColor },
            ticks: {
              callback: (v) => v + "h",
              font: { size: 11 },
              color: textMuted,
              stepSize: 1,
            },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        try { chartRef.current.destroy(); } catch (_) { }
        chartRef.current = null;
      }
    };
  }, [ready, velocity]);

  const noData = !hasRealData(velocity);

  return (
    <div className="pas-chart-wrap">
      {noData && (
        <div className="pas-chart-empty">
          <div className="pas-chart-empty-icon">📈</div>
          <p className="pas-chart-empty-title">No focus data yet</p>
          <p className="pas-chart-empty-sub">
            Watch course videos to start tracking your weekly learning time.
            Your first session will appear here.
          </p>
        </div>
      )}
      {/* Always render the canvas so Chart.js has a target — hide it when no data */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "160px",
          opacity: noData ? 0 : 1,
          transition: "opacity 0.3s ease",
          pointerEvents: noData ? "none" : "auto",
        }}
      >
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────── */
export default function ProAnalyticsSection({ focusTrend }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const isPro =
    user?.subscription?.plan === "PRO" &&
    user?.subscription?.status === "ACTIVE";

  const [analytics, setAnalytics] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [proRes, heatRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_URL || "/api"}/analytics/pro`,
            { withCredentials: true }
          ),
          axios.get(
            `${process.env.REACT_APP_API_URL || "/api"}/analytics/heatmap?days=28`,
            { withCredentials: true }
          ),
        ]);
        setAnalytics(proRes.data);
        setHeatmap(heatRes.data);
      } catch (err) {
        console.error("Analytics fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) {
    return (
      <div className="pas-container">
        <div className="pas-skeleton-row">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="pas-skeleton-card" />
          ))}
        </div>
        <div className="pas-skeleton-main" />
      </div>
    );
  }

  // Safe defaults — every value has a fallback so nothing crashes on empty data
  const metrics = analytics?.metrics ?? {
    weeklyVelocity: [0, 0, 0, 0],
    avgQuizScore: 0,
    readinessScore: 0,
    estimatedWeeks: "N/A",
    globalRanking: 100,
    syllabusCoverage: 0,
  };
  const raw = analytics?.rawData ?? {};
  const velocity = metrics.weeklyVelocity ?? [0, 0, 0, 0];
  const thisWeek = velocity[0] ?? 0;
  const lastWeek = velocity[1] ?? 0;
  const weekDelta = ((thisWeek - lastWeek) || 0).toFixed(1);
  const insights = deriveInsights(metrics, raw);
  const weakDay = findWeakDay(heatmap?.heatmap);

  const phases = raw.activeEnrollment?.track?.phases ?? [];
  const phaseProgress = raw.activeEnrollment?.phaseProgress ?? [];

  const isCompletelyNewUser =
    !hasRealData(velocity) &&
    !metrics.avgQuizScore &&
    !metrics.readinessScore;

  return (
    <div className="pas-container">
      <div className="pas-header">
        <h2 className="pas-title">Analytics</h2>
        {isPro && <span className="pas-pro-tag">PRO</span>}
      </div>

      {/* ── New user welcome state ───────────────────────── */}
      {isCompletelyNewUser && (
        <div className="pas-welcome-banner">
          <span className="pas-welcome-icon">🚀</span>
          <div>
            <p className="pas-welcome-title">Your analytics start here</p>
            <p className="pas-welcome-sub">
              Watch course videos or complete track phases to start seeing your
              focus time, quiz scores, and readiness data. Come back after your
              first session.
            </p>
          </div>
        </div>
      )}

      {/* ── Stat row ─────────────────────────────────────── */}
      {!isCompletelyNewUser && (
        <div className="pas-stat-row">
          <div className="pas-stat-card">
            <div className="pas-stat-label">This week</div>
            <div className="pas-stat-val">
              {thisWeek > 0 ? thisWeek.toFixed(1) + "h" : "—"}
            </div>
            {lastWeek > 0 ? (
              <div
                className={`pas-stat-delta ${Number(weekDelta) >= 0 ? "up" : "down"
                  }`}
              >
                {Number(weekDelta) >= 0 ? "+" : ""}
                {weekDelta}h vs last week
              </div>
            ) : (
              <div className="pas-stat-delta neutral">no data yet</div>
            )}
          </div>

          <div className="pas-stat-card">
            <div className="pas-stat-label">Readiness</div>
            <div className="pas-stat-val">
              {metrics.readinessScore > 0 ? metrics.readinessScore + "%" : "—"}
            </div>
            <div
              className={`pas-stat-delta ${metrics.readinessScore >= 80
                  ? "up"
                  : metrics.readinessScore > 0
                    ? "neutral"
                    : "neutral"
                }`}
            >
              {metrics.readinessScore >= 80
                ? "interview ready"
                : metrics.readinessScore > 0
                  ? "building up"
                  : "enroll in a track"}
            </div>
          </div>

          <div className="pas-stat-card">
            <div className="pas-stat-label">Quiz avg</div>
            <div className="pas-stat-val">
              {metrics.avgQuizScore > 0 ? metrics.avgQuizScore + "%" : "—"}
            </div>
            <div
              className={`pas-stat-delta ${metrics.avgQuizScore >= 70
                  ? "up"
                  : metrics.avgQuizScore > 0
                    ? "down"
                    : "neutral"
                }`}
            >
              {metrics.avgQuizScore >= 70
                ? "above threshold"
                : metrics.avgQuizScore > 0
                  ? "below 70% target"
                  : "no quizzes yet"}
            </div>
          </div>

          <div className="pas-stat-card">
            <div className="pas-stat-label">Est. completion</div>
            <div className="pas-stat-val">
              {metrics.estimatedWeeks !== "N/A" && metrics.estimatedWeeks > 0
                ? metrics.estimatedWeeks + "w"
                : "—"}
            </div>
            <div className="pas-stat-delta neutral">
              {metrics.estimatedWeeks !== "N/A" && metrics.estimatedWeeks > 0
                ? "at current pace"
                : "start to get estimate"}
            </div>
          </div>
        </div>
      )}

      {/* ── Coach banners ────────────────────────────────── */}
      {insights.map((ins, i) => (
        <div key={i} className={`pas-insight pas-insight--${ins.type}`}>
          <div className="pas-insight-text">
            {ins.text}
            {ins.action && (
              <button className="pas-insight-btn">
                {ins.action} →
              </button>
            )}
          </div>
        </div>
      ))}

      {/* ── Velocity chart ───────────────────────────────── */}
      <div className="pas-section-card">
        <div className="pas-section-head">
          <span className="pas-section-title">Focus time — last 4 weeks</span>
          <span className="pas-section-meta">goal: 5h/week</span>
        </div>
        <VelocityChart velocity={velocity} />
      </div>

      {/* ── Readiness + phases ───────────────────────────── */}
      <div className="pas-section-card">
        <div className="pas-section-head">
          <span className="pas-section-title">Track readiness</span>
          <span className="pas-readiness-val">
            {metrics.readinessScore > 0 ? metrics.readinessScore + "%" : "—"}
          </span>
        </div>

        {metrics.readinessScore > 0 ? (
          <>
            <div className="pas-score-track">
              <div
                className="pas-score-fill pas-score-fill--green"
                style={{ width: `${metrics.readinessScore}%` }}
              />
              <div
                className="pas-score-marker"
                style={{ left: "80%" }}
                title="Interview ready threshold"
              />
            </div>
            <div className="pas-readiness-caption">
              {metrics.readinessScore >= 80
                ? "You're above the interview-ready threshold. Apply now."
                : `${80 - metrics.readinessScore}% more to reach interview-ready. ${typeof metrics.estimatedWeeks === "number"
                  ? `~${Math.ceil(metrics.estimatedWeeks * 0.6)} weeks at 5h/week.`
                  : ""
                }`}
            </div>
          </>
        ) : (
          <div className="pas-empty-mini">
            Complete track phases to see your readiness score build up here.
          </div>
        )}

        {phases.length > 0 && (
          <div className="pas-phases">
            {phases.map((ph, i) => {
              const prog = phaseProgress.find((p) => p.phaseIndex === i);
              const done = prog?.completed;
              const pct = done
                ? 100
                : prog
                  ? Math.round(
                    ((prog.completedResources?.length || 0) /
                      (ph.resources?.length || 1)) *
                    100
                  )
                  : 0;
              return (
                <div
                  key={i}
                  className={`pas-phase-pill ${done ? "done" : pct > 0 ? "active" : ""
                    }`}
                >
                  Phase {i + 1} {done ? "✓" : pct > 0 ? `${pct}%` : ""}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Heatmap ──────────────────────────────────────── */}
      <div className="pas-section-card">
        <div className="pas-section-head">
          <span className="pas-section-title">Activity — last 28 days</span>
          <span className="pas-section-meta">
            Consistency:{" "}
            <strong>{heatmap?.consistencyScore ?? "—"}%</strong>
          </span>
        </div>

        {heatmap?.heatmap?.length > 0 && hasRealData(
          heatmap.heatmap.map((d) => d.minutes)
        ) ? (
          <>
            <div className="pas-heatmap">
              {heatmap.heatmap.map((d, i) => {
                const max = Math.max(
                  ...heatmap.heatmap.map((x) => x.minutes),
                  1
                );
                const t = d.minutes / max;
                let bg;
                if (d.minutes === 0) bg = "var(--pas-hm-empty, rgba(0,0,0,0.06))";
                else if (t > 0.7) bg = "#185FA5";
                else if (t > 0.4) bg = "#378ADD";
                else bg = "#B5D4F4";
                return (
                  <div
                    key={i}
                    className="pas-hm-cell"
                    style={{ background: bg }}
                    title={`${new Date(d.date).toDateString()}: ${d.minutes} min`}
                  />
                );
              })}
            </div>
            {weakDay && (
              <div className="pas-heatmap-note">
                {weakDay} is consistently your weakest day. One session there
                would add ~14% to your consistency score.
              </div>
            )}
          </>
        ) : (
          <div className="pas-empty-mini">
            No activity yet. Sessions you complete will fill in here day by day.
          </div>
        )}
      </div>

      {/* ── PRO-gated: global ranking ─────────────────────── */}
      <div className={`pas-section-card ${!isPro ? "pas-gated" : ""}`}>
        <div className="pas-section-head">
          <span className="pas-section-title">Global ranking</span>
          {isPro && (
            <span className="pas-section-meta">
              Top {metrics.globalRanking}%
            </span>
          )}
        </div>
        {isPro ? (
          <>
            <div className="pas-score-track">
              <div
                className="pas-score-fill"
                style={{
                  width: `${100 - metrics.globalRanking}%`,
                  background: "#534AB7",
                }}
              />
            </div>
            <div className="pas-readiness-caption">
              You're ahead of {100 - metrics.globalRanking}% of learners on
              this track. Completing Phase{" "}
              {(raw.activeEnrollment?.currentPhase || 1) + 1} moves you to top
              10%.
            </div>
          </>
        ) : (
          <div className="pas-gate-cta">
            <span className="pas-gate-text">
              See how you rank against all learners and what it takes to reach
              top 10%.
            </span>
            <button
              className="pas-gate-btn"
              onClick={() => navigate("/upgrade")}
            >
              Unlock PRO →
            </button>
          </div>
        )}
      </div>

      {/* ── Personalised next steps ───────────────────────── */}
      {!isCompletelyNewUser && (
        <div className="pas-section-card">
          <div
            className="pas-section-title"
            style={{ marginBottom: "12px" }}
          >
            Personalised next steps
          </div>
          <div className="pas-tips">
            <div className="pas-tip">
              <div className="pas-tip-num">1</div>
              <div>
                <div className="pas-tip-body">
                  {metrics.syllabusCoverage > 0 && metrics.syllabusCoverage < 100
                    ? `You're ${metrics.syllabusCoverage}% through the syllabus. Pushing to 5h this week closes the gap ${Math.ceil((metrics.estimatedWeeks || 10) * 0.4)} weeks faster.`
                    : metrics.syllabusCoverage >= 100
                      ? "You've completed the full syllabus. Start applying — your profile is ready for recruiters."
                      : "Enroll in a track and watch the first phase resources to generate your personalised plan."}
                </div>
                <div className="pas-tip-meta">
                  Impact: faster completion · higher readiness score
                </div>
              </div>
            </div>

            {metrics.avgQuizScore > 0 && metrics.avgQuizScore < 70 && (
              <div className="pas-tip">
                <div className="pas-tip-num">2</div>
                <div>
                  <div className="pas-tip-body">
                    Your quiz average is below 70%. Retake the lowest-scoring
                    quiz before moving to the next phase — it compounds.
                  </div>
                  <div className="pas-tip-meta">
                    Impact: closes knowledge gaps before they block you
                  </div>
                </div>
              </div>
            )}

            {weakDay && (
              <div className="pas-tip">
                <div className="pas-tip-num">
                  {metrics.avgQuizScore > 0 && metrics.avgQuizScore < 70
                    ? "3"
                    : "2"}
                </div>
                <div>
                  <div className="pas-tip-body">
                    {weakDay} is your dead zone — zero activity across the last
                    4 weeks. One 25-min session there completes your weekly
                    rhythm.
                  </div>
                  <div className="pas-tip-meta">Impact: +14% consistency score</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}